import {
  Asset,
  CustomerWithDriverOnboarding,
} from '../../../../../../../types/apiTypes';
import MaintenanceReminders from '../customer-assets/MaintenanceReminders';
import MaintenanceRecords from './MaintenanceRecords';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isPreActiveCustomer } from '../../../home/utils/demoData';
import { useUser } from '../../../../../../../utils/UserContext';
import { useMemo } from 'react';

interface AssetMaintenanceProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  selectedAsset: any | null;
  setSelectedAsset: (asset: Asset) => void;
  assetImageUrl: string;
  setAssetImageUrl: (url: string) => void;
}

function AssetMaintenance({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  selectedAsset,
  setSelectedAsset,
  assetImageUrl,
  setAssetImageUrl,
}: AssetMaintenanceProps) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  const { userRecord } = useUser();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  // Query keys
  const maintenanceRecordsKey = [
    'maintenanceRecords',
    selectedAsset?.truck_id || selectedAsset?.trailer_id,
    selectedAsset?.asset_type,
  ];
  const maintenanceRemindersKey = [
    'maintenanceReminders',
    selectedAsset?.truck_id || selectedAsset?.trailer_id,
    selectedAsset?.asset_type,
  ];

  // Fetch functions
  const fetchMaintenanceRecords = async () => {
    const response = await callProtectedApi(
      `/maintenanceRecords/${selectedAsset?.asset_type}/${selectedAsset?.truck_id || selectedAsset?.trailer_id}`,
      'get',
      {},
      'Error occurred while fetching maintenance records for this asset.'
    );
    return response?.data?.data?.maintenance_records || [];
  };

  const fetchMaintenanceReminders = async () => {
    const response = await callProtectedApi(
      `/maintenanceReminders/${selectedAsset?.asset_type}/${selectedAsset?.truck_id || selectedAsset?.trailer_id}`,
      'get',
      {},
      'Error occurred while fetching maintenance reminders for this asset.'
    );
    return response?.data?.data?.maintenance_reminders || [];
  };

  // Queries
  const { data: maintenanceRecords = [], isLoading: loadingRecords } = useQuery(
    {
      queryKey: maintenanceRecordsKey,
      queryFn: fetchMaintenanceRecords,
      enabled:
        !!(selectedAsset?.truck_id || selectedAsset?.trailer_id) &&
        !isPreActive,
    }
  );

  const { data: maintenanceReminders = [], isLoading: loadingReminders } =
    useQuery({
      queryKey: maintenanceRemindersKey,
      queryFn: fetchMaintenanceReminders,
      enabled:
        !!(selectedAsset?.truck_id || selectedAsset?.trailer_id) &&
        !isPreActive,
    });

  // Function to invalidate queries (pass this down to child components)
  const refreshMaintenanceData = () => {
    queryClient.invalidateQueries({ queryKey: maintenanceRecordsKey });
    queryClient.invalidateQueries({ queryKey: maintenanceRemindersKey });
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex h-full w-full flex-col items-center gap-4 align-middle`}
          >
            <MaintenanceReminders
              isPreActive={isPreActive}
              maintenanceRemindersData={maintenanceReminders}
              addClass={'h-[50%]'}
              selectedAsset={selectedAsset}
              maintenanceRecords={maintenanceRecords}
              loadingReminders={loadingReminders}
              onRecordChange={refreshMaintenanceData}
            />
            <MaintenanceRecords
              maintenanceRecordsData={maintenanceRecords}
              addClass={'h-[50%]'}
              selectedAsset={selectedAsset}
              loadingRecords={loadingRecords}
              onRecordChange={refreshMaintenanceData}
            />
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex h-full w-full flex-col items-center gap-4 pb-[16px] align-middle`}
          >
            <MaintenanceReminders
              maintenanceRemindersData={maintenanceReminders}
              addClass={'h-auto max-h-[80vh]'}
              selectedAsset={selectedAsset}
              maintenanceRecords={maintenanceRecords}
              loadingReminders={loadingReminders}
              onRecordChange={refreshMaintenanceData}
              isPreActive={isPreActive}
            />
            <MaintenanceRecords
              maintenanceRecordsData={maintenanceRecords}
              addClass={'h-auto max-h-[80vh]'}
              selectedAsset={selectedAsset}
              loadingRecords={loadingRecords}
              onRecordChange={refreshMaintenanceData}
            />
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex h-full w-full flex-col items-center gap-4 pb-[16px] align-middle`}
          >
            <MaintenanceReminders
              maintenanceRemindersData={maintenanceReminders}
              addClass={'h-auto max-h-[80vh]'}
              selectedAsset={selectedAsset}
              maintenanceRecords={maintenanceRecords}
              loadingReminders={loadingReminders}
              onRecordChange={refreshMaintenanceData}
              isPreActive={isPreActive}
            />
            <MaintenanceRecords
              maintenanceRecordsData={maintenanceRecords}
              addClass={'h-auto max-h-[80vh]'}
              selectedAsset={selectedAsset}
              loadingRecords={loadingRecords}
              onRecordChange={refreshMaintenanceData}
            />
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default AssetMaintenance;
